import { Component, inject, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { NotificationCenterService } from '../../core/services/notification-center.service';
import { NotificationCenter, NotificationType } from '../../core/interfaces/notification';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
  standalone: false
})
export class NotificationBellComponent {
  private readonly notificationService = inject(NotificationCenterService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  unreadCount$ = this.notificationService.unreadCount$;
  notifications$ = this.notificationService.notifications$;
  loading$ = this.notificationService.loading$;

  @ViewChild(NgbDropdown) private dropdown!: NgbDropdown;

  private maxPreviewItems = 5;

  // Called by ngbDropdown's (openChange) event — reliable even when
  // the dropdown closes via outside click (which skips toggleDropdown).
  onDropdownOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.notificationService.loadNotifications(true);
    }
  }

  viewAllNotifications(): void {
    this.dropdown.close();
    this.router.navigate(['/notifications']);
  }

  markAsRead(notification: NotificationCenter, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification.uuid);
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  handleNotificationClick(notification: NotificationCenter): void {
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.uuid);
    }
    this.dropdown.close();

    this.notificationService.navigateToNotification(notification);
  }

  getPreviewNotifications(notifications: NotificationCenter[]): NotificationCenter[] {
    return notifications.slice(0, this.maxPreviewItems);
  }

  getNotificationIcon(notification: NotificationCenter): string {
    switch (notification.type) {
      case NotificationType.BOOKING:
        return 'bed-double';
      case NotificationType.SECURITY:
        return 'shield-alert';
      case NotificationType.HOTEL_INFO:
        return 'info';
      case NotificationType.HOTEL_STATUS:
        return 'building-2';
      case NotificationType.HOTEL_SETTINGS:
        return 'settings';
      case NotificationType.SYSTEM:
        return 'bell';
      default:
        return 'bell';
    }
  }

  getNotificationColor(notification: NotificationCenter): string {
    switch (notification.type) {
      case NotificationType.BOOKING:
        return 'text-primary';
      case NotificationType.SECURITY:
        return 'text-danger';
      case NotificationType.HOTEL_INFO:
        return 'text-info';
      case NotificationType.HOTEL_STATUS:
        return 'text-warning';
      case NotificationType.HOTEL_SETTINGS:
        return 'text-secondary';
      case NotificationType.SYSTEM:
        return 'text-muted';
      default:
        return 'text-muted';
    }
  }

  truncateMessage(messageKey: string, maxLength = 60): string {
    const translated = this.translate.instant(messageKey);
    return translated.length > maxLength ? translated.substring(0, maxLength) + '...' : translated;
  }

  trackByNotificationId(_index: number, notification: NotificationCenter): string {
    return notification.uuid;
  }
}
